import RootStore from "store/rootStore";
import { darken, lighten } from "@material-ui/core/styles/colorManipulator";
import { fabric } from "fabric";
import _ from "lodash";

const SEPARATOR = "||";

const draw = (poly: any, store: RootStore) => {
  // console.log("drawPolygon ", poly.labelValue)

  const {
    fabricSegmentationStore: { canvas, clearCanvasObjectsByName },
    labelerSegmentationUiStore: {
      tagSelectedIndex,
      selectMode,
      setActivePolygon,
      activePolygon,
    },
    labelerSegmentationStore: { tags },
  } = store;

  const fill = _getFillColor(poly, tags, tagSelectedIndex);
  const name = `${poly.labelValue}`;

  const polygon = new fabric.Polygon(poly.points, {
    name,
    fill: !poly.done ? lighten(fill, 0.7) : fill,
    // opacity: selected ? 0.6 : 0.4,
    opacity: 0.4,
    // opacity: !poly.done ? 0.2 : 0.4,
    selectable: selectMode,
    hoverCursor: selectMode ? "pointer" : "normal",
    objectCaching: false,
    stroke: darken(fill, 0.5),
    strokeWidth: 3,
    hasBorders: true,
    hasControls: true,
    transparentCorners: false,
  });

  if (selectMode && poly.visible) {
    _addControls(poly, fill, polygon, store);
    if (!poly.done) {
      setActivePolygon(polygon);
      canvas.setActiveObject(polygon);
    }
  }

  clearCanvasObjectsByName(name);
  canvas.add(polygon);

  if (!poly.done && !selectMode) {
    _addFirstPoint(poly, fill, canvas, store);
    if (poly.points.length >= 2) {
      _addLastPoint(poly, fill, canvas, store);
    }
  }

  if (activePolygon?.name === name && poly.done) {
    setActivePolygon(polygon);
    canvas.setActiveObject(polygon);
  }
  canvas.requestRenderAll();
};

//// PRIVATE /////////////////////////////////:

const _getFillColor = (
  poly: any,
  tags: Array<any>,
  tagSelectedIndex: number
) => {
  const labelForPoly = tags.find(
    (l: any) => l.legend === poly.labelValue.split(SEPARATOR)[0]
  );
  const fill = labelForPoly ? labelForPoly.color : tags[tagSelectedIndex].color;

  return fill;
};

const _addFirstPoint = (
  poly: any,
  fill: any,
  canvas: fabric.Canvas,
  store: RootStore
) => {
  const { clearCanvasObjectsByName } = store.fabricSegmentationStore;

  const { zoom } = store.labelerSegmentationUiStore;

  const polyFirstPoint = poly.points[0];
  if (polyFirstPoint) {
    const { x, y } = polyFirstPoint;
    const name = `${poly.labelValue}${SEPARATOR}_first`;
    const newFirstPoint = new fabric.Rect({
      name,
      width: 10 / zoom,
      height: 10 / zoom,
      fill,
      selectable: false,
      originY: "center",
      originX: "center",
      top: y,
      left: x,
    });
    newFirstPoint.hoverCursor = "crosshair";
    // setFirstPoint(newFirstPoint)
    clearCanvasObjectsByName(name);
    canvas.add(newFirstPoint);
  }
};

const _addLastPoint = (
  poly: any,
  fill: any,
  canvas: fabric.Canvas,
  store: RootStore
) => {
  const { clearCanvasObjectsByName } = store.fabricSegmentationStore;

  const { zoom } = store.labelerSegmentationUiStore;

  // if (lastPoint) {
  //     canvas.remove(lastPoint);
  // }

  const polyLastPoint = poly.points[poly.points.length - 1];
  if (polyLastPoint) {
    const { x, y } = polyLastPoint;
    const name = `${poly.labelValue}${SEPARATOR}_last`;
    const newLastPoint = new fabric.Circle({
      radius: 5 / zoom,
      fill,
      selectable: false,
      originY: "center",
      originX: "center",
      top: y,
      left: x,
      name,
    });
    newLastPoint.hoverCursor = "crosshair";
    // setLastPoint(newLastPoint)
    clearCanvasObjectsByName(name);
    canvas.add(newLastPoint);
  }
};

const _addControls = (
  poly: any,
  fill: any,
  polygon: fabric.Polygon,
  store: RootStore
) => {
  const { deleteMode, resizeMode } = store.labelerSegmentationUiStore;

  const { stickMode } = store.stickTwoPolygonsStore;

  const lastControl = poly.points.length - 1;
  polygon.cornerStyle = "circle";
  polygon.cornerColor = deleteMode ? "rgba(255,0,0,1)" : darken(fill, 0.4);
  if (stickMode) {
    polygon.lockMovementX = true;
    polygon.lockMovementY = true;
  } else {
    polygon.lockMovementX = false;
    polygon.lockMovementY = false;
  }
  if (resizeMode) {
    polygon.controls = fabric.Object.prototype.controls;
  } else {
    polygon.controls = poly.points.reduce(function(
      acc: any,
      point: any,
      index: any
    ) {
      const anchorIndex = index > 0 ? index - 1 : lastControl;
      acc["p" + index] = new fabric.Control({
        positionHandler: polygonPositionHandler,
        actionHandler: anchorWrapper(poly, anchorIndex, store, actionHandler),
        //@ts-ignore
        mouseUpHandler: mouseUpWrapper(poly, store),
        actionName: "modifyPolygon",
        cursorStyle: deleteMode ? "not-allowed" : "move",
        //@ts-ignore
        pointIndex: index,
      });
      return acc;
    },
    {});
  }
};

// define a function that can locate the controls.
// this function will be used both for drawing and for interaction.
function polygonPositionHandler(dim: any, finalMatrix: any, fabricObject: any) {
  //@ts-ignore
  var x = fabricObject.points[this.pointIndex].x - fabricObject.pathOffset.x,
    //@ts-ignore
    y = fabricObject.points[this.pointIndex].y - fabricObject.pathOffset.y;

  return fabric.util.transformPoint(
    //@ts-ignore
    { x: x, y: y },
    fabric.util.multiplyTransformMatrices(
      fabricObject.canvas?.viewportTransform || [1, 0, 0, 1, 0, 0],
      // [1, 0, 0, 1, 0, 0],
      fabricObject.calcTransformMatrix()
    )
  );
}

function _getObjectSizeWithStroke(object: any) {
  var stroke = new fabric.Point(
    object.strokeUniform ? 1 / object.scaleX : 1,
    object.strokeUniform ? 1 / object.scaleY : 1
  ).multiply(object.strokeWidth);
  return new fabric.Point(object.width + stroke.x, object.height + stroke.y);
}

function actionHandler(eventData: any, transform: any, x: any, y: any) {
  var polygon = transform.target,
    currentControl = polygon.controls[polygon.__corner],
    mouseLocalPosition = polygon.toLocalPoint(
      new fabric.Point(x, y),
      "center",
      "center"
    ),
    polygonBaseSize = _getObjectSizeWithStroke(polygon),
    size = polygon._getTransformedDimensions(0, 0),
    finalPointPosition = {
      x:
        (mouseLocalPosition.x * polygonBaseSize.x) / size.x +
        polygon.pathOffset.x,
      y:
        (mouseLocalPosition.y * polygonBaseSize.y) / size.y +
        polygon.pathOffset.y,
    };
  polygon.points[currentControl.pointIndex] = finalPointPosition;
  return { actionPerformed: true, finalPointPosition };
}

// define a function that can keep the polygon in the same position when we change its
// width/height/top/left.
function anchorWrapper(poly: any, anchorIndex: any, store: RootStore, fn: any) {
  return function(eventData: any, transform: any, x: any, y: any) {
    var fabricObject = transform.target,
      absolutePoint = fabric.util.transformPoint(
        new fabric.Point(
          fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x,
          fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y
        ),
        fabricObject.calcTransformMatrix()
      ),
      { actionPerformed, finalPointPosition } = fn(eventData, transform, x, y),
      // newDim = fabricObject._setPositionDimensions({}),
      polygonBaseSize = _getObjectSizeWithStroke(fabricObject),
      newX =
        (fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x) /
        polygonBaseSize.x,
      newY =
        (fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y) /
        polygonBaseSize.y;

    fabricObject.setPositionByOrigin(absolutePoint, newX + 0.5, newY + 0.5);
    return actionPerformed;
  };
}

const mouseUpWrapper = (poly: any, store: RootStore) => (
  event: MouseEvent,
  transf: fabric.Transform
) => {
  const {
    stickMode,
    firstPolyFinished,
    secondPolyFinished,
    resetPolyStick,
    selectPointFirstPolygon,
    selectPointSecondPolygon,
  } = store.stickTwoPolygonsStore;
  const { deleteMode, updatePolygonPoints } = store.labelerSegmentationUiStore;

  if (deleteMode) {
    const pointIndex = parseInt(transf.corner.replace("p", ""), 10);
    const newPoints = poly.points.filter(
      (p: any, idx: number) => idx !== pointIndex
    );
    updatePolygonPoints(poly, newPoints);
    resetPolyStick();
  }
  if (stickMode && !deleteMode) {
    const pointIndex = parseInt(transf.corner.replace("p", ""), 10);
    if (!firstPolyFinished) {
      selectPointFirstPolygon(pointIndex, poly);
    }
    if (firstPolyFinished && !secondPolyFinished) {
      selectPointSecondPolygon(pointIndex, poly);
    }
  }
};

export default {
  draw,
};
